'use client';

import { useEffect, useState } from 'react';
import { LogOut, Mail } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { Button, Gleam, IconButton, Menu, UnstyledButton } from '@zillow/constellation-10';
import { UserAvatar } from '@/src/components/ui/UserAvatar';
import { Link } from '@/src/i18n/navigation';
import { LucideIcon } from '@/src/components/LucideIcon';
import {
  USER_ACCOUNT_NAV_ORDER,
  USER_NAV_HREFS,
  USER_NAV_ICONS,
  type UserNavKey,
} from '@/src/components/features/user/navigation/userNavigation';
import { useCompatRouter } from '@/src/lib/router-compat';
import { useCurrentUser, useLogout } from '@/src/lib/hooks/query/useUserQuery';
import { useHasUnreadMessages } from '@/src/lib/hooks/query/useHasUnreadMessages';
import { getUserInitials } from '@/src/lib/utils/getUserInitials';
import { emitTrackHeaderLoginEvent } from '@/src/analytics/hotpads/events';
import { localizePostLoginRedirect, sanitizePostLoginRedirect } from '@/src/lib/auth/post-login-redirect';
import type { SupportedLocale } from '@/src/i18n/routing';
import { SkeletonBlock } from '@/src/components/ui/SkeletonBlock';
import {
  authButton,
  authPlaceholder,
  authPlaceholderSkeleton,
  cluster,
  identityLabelStack,
  identityName,
  identitySubtitle,
} from './userDrawer.styles';

function getSignInHref(currentPage: string | null, locale: SupportedLocale): string {
  return currentPage
    ? `/login?redirect=${encodeURIComponent(localizePostLoginRedirect(currentPage, locale))}`
    : '/login';
}

export function UserDrawer() {
  const tUser = useTranslations('user');
  const tNav = useTranslations('nav');
  const tDrawer = useTranslations('userDrawer');
  const locale = useLocale() as SupportedLocale;
  const router = useCompatRouter();
  const [locationHash, setLocationHash] = useState('');
  const currentPath = router.asPath.split('#', 1)[0] ?? '/';
  const currentPage = sanitizePostLoginRedirect(`${currentPath}${locationHash}`);
  const signInHref = getSignInHref(currentPage, locale);

  useEffect(() => {
    const syncLocationHash = () => setLocationHash(window.location.hash);

    syncLocationHash();
    window.addEventListener('hashchange', syncLocationHash);
    return () => window.removeEventListener('hashchange', syncLocationHash);
  }, [router.asPath]);

  const { data: currentUser } = useCurrentUser();
  const hasResolvedIdentity = currentUser !== undefined;
  const isLoggedIn = currentUser?.loggedIn ?? false;
  const hasUnread = useHasUnreadMessages();
  const userEmail = currentUser?.loggedIn ? currentUser.data.email : '';
  const userFirstName = currentUser?.loggedIn ? currentUser.data.firstName : '';
  const userLastName = currentUser?.loggedIn ? currentUser.data.lastName : '';
  const userFullName = [userFirstName, userLastName].filter(Boolean).join(' ');
  const userDisplayName = userFullName || userEmail.split('@')[0] || userEmail;
  const userInitials = getUserInitials(userFirstName, userLastName) || userDisplayName[0]?.toUpperCase() || '';
  const userPhotoUrl = currentUser?.data.photoUrl;
  const editProfileLabel = tUser('editMyProfile');

  const logoutMutation = useLogout();

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        router.push('/');
      },
    });
  };

  const signedInLabels = {
    profile: tUser('profile'),
    inbox: tUser('inbox'),
    favorites: tUser('myFavorites'),
    savedSearches: tNav('savedSearches'),
    viewed: tUser('searchHistory'),
    contacted: tUser('contacted'),
    hidden: tUser('hidden'),
    notifications: tUser('notifications'),
  } satisfies Record<UserNavKey, string>;

  const signedInLinks = USER_ACCOUNT_NAV_ORDER.map((navKey) => ({
    href: USER_NAV_HREFS[navKey],
    label: signedInLabels[navKey],
    icon: USER_NAV_ICONS[navKey],
  }));

  const mailIcon = <LucideIcon icon={Mail} />;
  const messagesButton = (
    <IconButton
      asChild
      type="button"
      tone="neutral"
      emphasis="bare"
      size="sm"
      shape="circle"
      title={tNav('openMessages')}
      icon={
        hasUnread ? (
          <Gleam appearance="dot" count={1} dotProps={{ label: tNav('unreadMessages') }} tone="default">
            {mailIcon}
          </Gleam>
        ) : (
          mailIcon
        )
      }
    >
      <Link href="/messages" />
    </IconButton>
  );

  const authControl = !hasResolvedIdentity ? (
    <span className={authPlaceholder} role="status" aria-busy="true" aria-label={tDrawer('loadingIdentity')}>
      <SkeletonBlock className={authPlaceholderSkeleton} />
    </span>
  ) : isLoggedIn ? (
    <Menu
      placement="bottom-end"
      dividers
      trigger={
        <UnstyledButton aria-label={tDrawer('openSignedIn')}>
          <UserAvatar size="sm" tone="default" aria-hidden photoUrl={userPhotoUrl} initials={userInitials} />
        </UnstyledButton>
      }
      content={
        <>
          <Menu.Group>
            <Menu.Item asChild>
              <Link href="/user" aria-label={`${userDisplayName} — ${editProfileLabel}`}>
                <Menu.ItemIcon
                  css={{ alignSelf: 'center' }}
                  render={<LucideIcon icon={USER_NAV_ICONS.profile} size="sm" asRender />}
                />
                <Menu.ItemLabel>
                  <span className={identityLabelStack}>
                    <span className={identityName}>{userDisplayName}</span>
                    <span className={identitySubtitle}>{editProfileLabel}</span>
                  </span>
                </Menu.ItemLabel>
              </Link>
            </Menu.Item>
          </Menu.Group>

          <Menu.Group>
            {signedInLinks.map((link) => (
              <Menu.Item key={link.href} asChild>
                <Link href={link.href}>
                  <Menu.ItemIcon
                    css={{ alignSelf: 'center' }}
                    render={<LucideIcon icon={link.icon} size="sm" asRender />}
                  />
                  <Menu.ItemLabel>{link.label}</Menu.ItemLabel>
                </Link>
              </Menu.Item>
            ))}
          </Menu.Group>

          <Menu.Group>
            <Menu.Item onClick={handleLogout} disabled={logoutMutation.isPending}>
              <Menu.ItemIcon css={{ alignSelf: 'center' }} render={<LucideIcon icon={LogOut} size="sm" asRender />} />
              <Menu.ItemLabel>{logoutMutation.isPending ? tUser('signingOut') : tUser('signOut')}</Menu.ItemLabel>
            </Menu.Item>
          </Menu.Group>
        </>
      }
    />
  ) : (
    <Button asChild tone="neutral" emphasis="bare" size="sm" className={authButton}>
      {/* signInHref stays unprefixed because next-intl's Link owns locale prefixing and navigation in both routers. */}
      {/* prefetch={false}: signInHref embeds the current page as a redirect
          param, so every route viewport-prefetches its own uncacheable /login
          RSC payload from the global header (HP-8669). */}
      <Link href={signInHref} onClick={emitTrackHeaderLoginEvent} prefetch={false}>
        {tNav('signIn')}
      </Link>
    </Button>
  );

  return (
    <div className={cluster}>
      {isLoggedIn && messagesButton}
      {authControl}
    </div>
  );
}

export default UserDrawer;
